<template>
  <div
    ref="containerRef"
    class="flex items-center  gap-2 overflow-hidden w-full"
    :class=" wrapTags ? 'flex-wrap' : 'flex-nowrap' "
  >
    <AM3Tag
      v-for="(tag, index) in processedTags"
      :key="index"
      :label="tag.label || tag"
      :type="type"
      :closable="closable"
      :data-test-id="generateDataTestId(dataTestId, 'tag', tag.label || tag)"
      :truncate="truncate"
      @close="handleClose(index, tag)"
    />
    <div
      v-if="hiddenCount > 0"
      v-tooltip="displayToolTip"
      class="inline-flex items-center px-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer h-6"
      :class="[typeClasses[type]]"
      :data-test-id="generateDataTestId(dataTestId, 'tag-count')"
    >
      +{{ hiddenCount }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue'
import type { PropType } from 'vue'
import { useStore } from 'vuex'
import AM3Tag from './AM3Tag.vue'
import type { TagType } from './AM3Tag.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import type { Tag } from '@/types/resources/tag'
import type { RootState } from '@/store/modules/types'

export interface TagItem {
  label: string
  value: any
}

const props = defineProps({
  tags: {
    type: Array,
    required: false,
    default: () => [],
  },
  fetchByOid: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String as PropType<TagType>,
    default: 'brand',
    validator: (value: string) => ['default', 'primary', 'secondary', 'success', 'warning', 'danger', 'brand', 'surface', 'outline'].includes(value),
  },
  dataTestId: {
    type: String,
    default: 'tags',
  },
  closable: {
    type: Boolean,
    default: false,
  },
  wrapTags: {
    type: Boolean,
    default: false,
  },
  truncate: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])

// Store access
const store = useStore<RootState>()

const containerRef = ref<HTMLElement | null>(null)
const visibleTags = ref<Array<string | TagItem>>([])
const hiddenCount = ref(0)
const fetchedTags = ref<Map<number, Tag>>(new Map())

const typeClasses = {
  default: 'bg-gray-100 text-gray-800',
  brand: 'bg-surfaces-informative text-font-brand',
  primary: 'bg-gray-100 text-gray-800',
  secondary: 'bg-gray-100 text-secondary',
  success: 'bg-green-50 text-green-700',
  warning: 'bg-yellow-50 text-yellow-700',
  danger: 'bg-red-50 text-red-700',
}

// Process tags to handle string[], number[] (OIDs), and TagItem[]
const processedTags = computed(() => {
  if (props.fetchByOid) {
    return visibleTags.value.map((tag) => {
      if (typeof tag === 'number') {
        const fetchedTag = fetchedTags.value.get(tag)
        return fetchedTag ? fetchedTag.name : `Tag ${tag}`
      }
      return typeof tag === 'string' ? tag : (tag as TagItem).label
    })
  }
  return visibleTags.value
})

// Get tag names from OIDs using the store
const getTagNamesFromOids = async (oids: number[]) => {
  if (oids.length === 0) return

  try {
    // Check if tags are already in the store
    const storeTags = store.state.tagModule?.tags || []
    const existingTags = storeTags.filter(tag => oids.includes(tag.oid))

    // Update our local cache with existing tags
    existingTags.forEach((tag) => {
      fetchedTags.value.set(tag.oid, tag)
    })

    // Find missing OIDs that need to be fetched
    const missingOids = oids.filter(oid => !fetchedTags.value.has(oid) && !existingTags.find(t => t.oid === oid))

    if (missingOids.length > 0) {
      // Fetch missing tags
      await store.dispatch('tagModule/FETCH_TAGS', {
        oids: missingOids,
        $select: ['oid', 'name', 'count'],
      })

      // Update cache with newly fetched tags
      const newTags = store.state.tagModule?.tags || []
      newTags.filter(tag => missingOids.includes(tag.oid)).forEach((tag) => {
        fetchedTags.value.set(tag.oid, tag)
      })
    }
  }
  catch (error) {
    console.error('Error fetching tags:', error)
  }
}

// Generate tooltip text for truncated tags
const displayToolTip = computed(() => {
  if (hiddenCount.value === 0) return null

  const allTagNames = props.tags.map((tag) => {
    if (typeof tag === 'number' && props.fetchByOid) {
      const fetchedTag = fetchedTags.value.get(tag)
      // this is the more elaborate view, with counts etc displayed.  Leaving it here for future use.
      // return fetchedTag ? `${fetchedTag.name}${fetchedTag.count ? ` (${fetchedTag.count} ${fetchedTag.count === 1 ? 'contact' : 'contacts'})` : ''}` : `Tag ${tag}`

      // for now, we just want tag names:
      return fetchedTag ? fetchedTag.name : null
    }
    return typeof tag === 'string' ? tag : (tag as TagItem).label
  })

  return allTagNames.join(', ')
})

const calculateVisibleTags = () => {
  if (props.wrapTags) {
    visibleTags.value = props.tags
    hiddenCount.value = 0
    return
  }
  if (!containerRef.value || !props.tags.length) return

  // Reset
  visibleTags.value = []
  hiddenCount.value = 0

  const containerWidth = containerRef.value.offsetWidth

  // Create a temp container to measure tags
  const tempContainer = document.createElement('div')
  tempContainer.className = 'flex items-center gap-2'
  tempContainer.style.visibility = 'hidden'
  tempContainer.style.position = 'absolute'
  tempContainer.style.width = `${containerWidth}px`
  document.body.appendChild(tempContainer)

  let currentWidth = 0
  let visibleCount = 0
  const gap = 8 // 2px from the gap-2 class

  // Make the +N indicator tag to always reserve space for it
  const countTag = document.createElement('div')
  countTag.className = 'inline-flex items-center px-2 rounded-full text-xs font-medium h-6'
  countTag.textContent = '+99' // A reasonably wide example
  tempContainer.appendChild(countTag)
  const countTagWidth = countTag.offsetWidth
  tempContainer.removeChild(countTag)

  // Test each tag
  for (let i = 0; i < props.tags.length; i++) {
    const tag = document.createElement('div')
    tag.className = 'inline-flex items-center px-2 rounded-full text-xs font-medium h-6'

    // Handle different tag types
    if (typeof props.tags[i] === 'number' && props.fetchByOid) {
      const fetchedTag = fetchedTags.value.get(props.tags[i] as number)
      tag.textContent = fetchedTag ? fetchedTag.name : `Tag ${props.tags[i]}`
    }
    else if (typeof props.tags[i] === 'string') {
      tag.textContent = props.tags[i] as string
    }
    else {
      tag.textContent = (props.tags[i] as TagItem).label
    }

    tempContainer.appendChild(tag)

    const tagWidth = tag.offsetWidth + gap

    // If this tag fits within the container width (minus space for count tag if needed)
    const isLastTag = i === props.tags.length - 1
    const needsCountTag = !isLastTag
    const availableWidth = needsCountTag ? containerWidth - countTagWidth - gap : containerWidth

    if (currentWidth + tagWidth <= availableWidth) {
      currentWidth += tagWidth
      visibleCount++
      tempContainer.removeChild(tag)
    }
    else {
      // This tag doesn't fit, so we're done
      tempContainer.removeChild(tag)
      break
    }
  }

  document.body.removeChild(tempContainer)

  // Set the visible tags and hidden count
  visibleTags.value = props.tags.slice(0, visibleCount)
  hiddenCount.value = props.tags.length - visibleCount
}

const handleClose = (index: number, tag: TagItem) => {
  emit('close', index, tag)
}

// Watch for changes in tags and fetch tag names if needed
watch(() => props.tags, async (newTags) => {
  if (props.fetchByOid && newTags.length > 0) {
    const oids = newTags.filter(tag => typeof tag === 'number') as number[]
    if (oids.length > 0) {
      await getTagNamesFromOids(oids)
    }
  }
  calculateVisibleTags()
}, { immediate: true })

// Watch for changes in fetchedTags and recalculate visible tags
watch(() => fetchedTags.value, () => {
  calculateVisibleTags()
}, { deep: true })

onMounted(() => {
  calculateVisibleTags()
  window.addEventListener('resize', calculateVisibleTags)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', calculateVisibleTags)
})
</script>
